import React, { useMemo } from 'react'
import { intl } from 'lang/intl'
import * as Yup from 'yup'

import { type ActionCardResponse } from '../blocs/useActionCards'
import TtFormik from '../../global/formik-forms'

import './ActionCardForm.scss'

interface ActionCardAudienceFormProps {
  card: ActionCardResponse
  onSubmit: (values: Record<string, any>) => void
}

const ActionCardForm = ({ card, onSubmit }: ActionCardAudienceFormProps) => {
  const initialValues = useMemo(() => {
    return (card.fields || []).reduce<Record<string, string | boolean>>(
      (acc, field) => {
        acc[field.id] = field.type === 'check' ? false : ''
        return acc
      },
      {}
    )
  }, [card.fields])

  const validationSchema = Yup.object(
    Object.fromEntries(
      card.fields.map((field) => [
        field.id,
        field.type === 'check'
          ? Yup.boolean()
          : field.type === 'text-short'
            ? Yup.string()
                .required('Required')
                .max(100, 'Maximum 100 characters')
            : Yup.string()
                .required('Required')
                .max(4000, 'Maximum 4000 characters'),
      ])
    )
  )

  type FormValues = Yup.InferType<typeof validationSchema>

  return (
    <TtFormik.Form<FormValues>
      label={intl.formatMessage({ defaultMessage: 'Answer Action Card Form' })}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <div className="action-card-options">
        {card.fields.map((field) => {
          switch (field.type) {
            case 'check': {
              return (
                <TtFormik.Checkbox
                  key={field.id}
                  className="action-card-checkbox"
                  name={`${field.id}`}
                  label={field.label}
                />
              )
            }
            case 'text-short': {
              return (
                <TtFormik.TextField
                  key={field.id}
                  className="action-card-text-field"
                  name={`${field.id}`}
                  label={field.label}
                  hideLabel
                  placeholder={field.label}
                  size="medium"
                />
              )
            }
            default: {
              return (
                <TtFormik.TextArea
                  key={field.id}
                  className="action-card-text-area"
                  name={`${field.id}`}
                  label={field.label}
                  hideLabel
                  placeholder={field.label}
                  lines={3}
                />
              )
            }
          }
        })}
      </div>

      <TtFormik.SubmitButton
        className="action-card-submit-btn"
        size="medium"
        variant="default"
        pill
      >
        {card.submitLabel}
      </TtFormik.SubmitButton>
    </TtFormik.Form>
  )
}

export { ActionCardForm }
